<script lang="ts" setup>
import { AppButton, AppTypography } from '@/core/components';
import { useSocialAuth, type SocialProvider } from '@/modules/auth/composables/useSocialAuth';

const { disabled = false } = defineProps<{
  disabled?: boolean;
}>();

const { startSocialLogin } = useSocialAuth();
const loadingProvider = ref<SocialProvider | null>(null);

function handleSocialLogin(provider: SocialProvider): void {
  loadingProvider.value = provider;
  startSocialLogin(provider);
}
</script>

<template>
  <div class="social-login">
    <div class="social-login__divider" aria-hidden="true">
      <span />
      <AppTypography element="span" type="caption" color="-tertiary">or</AppTypography>
      <span />
    </div>

    <AppButton
      block
      type="button"
      variant="outlined"
      color="-primary"
      class="social-login__button"
      data-testid="social-google"
      :loading="loadingProvider === 'google'"
      :disabled="disabled || Boolean(loadingProvider)"
      @click="handleSocialLogin('google')"
    >
      <template #prepend>
        <v-icon icon="mdi-google" size="20" />
      </template>
      Continue with Google
    </AppButton>

    <AppButton
      block
      type="button"
      variant="outlined"
      color="-primary"
      class="social-login__button"
      data-testid="social-apple"
      :loading="loadingProvider === 'apple'"
      :disabled="disabled || Boolean(loadingProvider)"
      @click="handleSocialLogin('apple')"
    >
      <template #prepend>
        <v-icon icon="mdi-apple" size="22" />
      </template>
      Continue with Apple
    </AppButton>
  </div>
</template>

<style lang="scss" scoped>
.social-login {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.social-login__divider {
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr auto 1fr;
  margin-bottom: 4px;

  span:first-child,
  span:last-child {
    border-top: 1px solid rgb(var(--v-theme-stroke));
    display: block;
  }
}

.social-login__button {
  min-height: 48px;

  :deep(.v-btn__content) {
    color: rgb(var(--v-theme--primary));
    font-weight: 700;
  }
}
</style>
